import { Component, inject, Input, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select } from '@ngxs/store';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { ICON } from '@core/util/icon';
import { MatterBusinessUser } from '@models/matter';
import {
	CostBreakdownComponent,
	FCTTitleInsuranceCostBreakdownData,
} from '../../../fct/components/cost-breakdown/cost-breakdown.component';
import { ChicagoFunctions } from '@functions/chicago.service';
import { take } from 'rxjs/operators';
import LogRocket from 'logrocket';
import {
	PdfModalComponent,
	PdfModalData,
} from '@components/pdf-modal/pdf-modal.component';
import { getMimeType } from '@core/util/mime';
import {
	MatterManualTitleInsurance,
	TitleInsuranceDocument,
	TitleInsurancePolicy,
	TitleInsuranceProvider,
} from '@models/title-insurance';
import {
	TitleInsurancePillData,
	getCounselFeeHover,
	getPremiumPillHover,
} from '@core/util/title-insurance';
import { SnackbarService } from 'src/app/shared/services/ui/snackbar.service';
import {
	AnalyticsEvent,
	AnalyticsService,
} from '@services/analytics/analytics.service';
import { DirectionOfFeeType } from '@models/businessSettings/directionOfFees';
import { TestHooks } from './ready-for-pickup.hooks';
import { DrawerService } from 'src/app/ui/drawer/drawer.service';

export interface ChicagoDocuments extends TitleInsuranceDocument {
	loading: boolean;
}

@UntilDestroy()
@Component({
	selector: 'app-chicago-ready-for-pickup',
	templateUrl: './ready-for-pickup.component.html',
	styleUrls: ['./ready-for-pickup.component.scss'],
	standalone: false,
})
export class ChicagoReadyForPickupComponent implements OnInit {
	testHooks = TestHooks;
	public ICON = ICON;
	public titleInsurance: MatterManualTitleInsurance;
	public displayPill = 'none';
	public premiumPillData: TitleInsurancePillData;
	public counselPillData: TitleInsurancePillData;
	public documents: ChicagoDocuments[] = [];
	@Input() status = 0;
	@Input() matterId: string;
	@Input() thirdPartyId: number;

	@Select(SelectedMatter.getTitleInsurance)
	titleInsurance$: Observable<MatterManualTitleInsurance>;
	@Select(SelectedMatter.titleInsurancePolicies) policies$: Observable<
		TitleInsurancePolicy[]
	>;
	@Select(SelectedMatter.titleInsuranceAmountDueByClient)
	amountDueByClient$: Observable<number>;
	@Select(SelectedMatter.getTitleInsuranceDirectionOfAmount)
	titleInsuranceDOA$: Observable<DirectionOfFeeType>;
	@SelectSnapshot(SelectedMatter.assignedLawyer) lawyer: MatterBusinessUser;

	drawerService = inject(DrawerService);

	constructor(
		private dialog: MatDialog,
		private chicagoFunction: ChicagoFunctions,
		private snackbarService: SnackbarService,
		private analytics: AnalyticsService,
	) {}

	ngOnInit() {
		combineLatest([this.titleInsurance$, this.titleInsuranceDOA$])
			.pipe(untilDestroyed(this))
			.subscribe(([_titleInsurance, titleInsuranceDOA]) => {
				if (_titleInsurance) {
					this.titleInsurance = _titleInsurance;
				}

				this.premiumPillData = getPremiumPillHover(titleInsuranceDOA);
				this.counselPillData = getCounselFeeHover(
					_titleInsurance,
					titleInsuranceDOA,
				);

				this.documents = _titleInsurance?.documents?.map((doc) => ({
					...doc,
					loading: false,
				}));
			});
	}

	async checkStatus(): Promise<void> {
		const { matterId, thirdPartyId } = this;
		if (matterId && thirdPartyId) {
			await firstValueFrom(
				this.chicagoFunction.getAllStatusDetails(thirdPartyId, matterId),
			);
		}
	}

	policyTypeString(type: string): string {
		if (type === 'lender') {
			return 'Lender policy';
		}

		if (type === 'owner') {
			return 'Owner policy';
		}

		return type;
	}

	showBreakdown() {
		this.analytics.logEvent(AnalyticsEvent.TITLE_INSURANCE_VIEW_FEE_BREAKDOWN, {
			provider: TitleInsuranceProvider.CHICAGO,
		});
		const data: FCTTitleInsuranceCostBreakdownData = {
			type: 'ORDER',
		};
		this.dialog.open(CostBreakdownComponent, { data });
	}

	togglePill(_displayPill: string) {
		this.displayPill = _displayPill;
	}

	async selectDoc(document: ChicagoDocuments) {
		try {
			document.loading = true;
			LogRocket.log(
				'Fetching Chicago Title Insurance Document',
				document.storageId,
			);

			const doc = await this.chicagoFunction
				.getDocument(document.storageId, this.lawyer.id)
				.pipe(take(1))
				.toPromise()
				.finally(() => (document.loading = false));

			LogRocket.log('Chicago document successfully downloaded');

			const data: PdfModalData = {
				base64Encoded: {
					data: doc.Document.DocumentRawStream,
					encoding: getMimeType[doc.Document.DocumentExtension.toLowerCase()],
				},
				fileName: doc.Document.FileName,
			};

			this.openPDF(data);
		} catch (err) {
			this.snackbarService.openSnackBar(
				{ text: 'Unable to get Chicago document', type: 'error' },
				{
					duration: 3000,
					panelClass: 'ml-96',
				},
			);
			LogRocket.error('Unable to get chicago document', err);
		}
	}

	openPDF(data: PdfModalData) {
		this.drawerService.openDrawer(PdfModalComponent, {
			data,
			title: data.fileName,
			noStyle: true,
		});
	}
}
